import React, {useState, useCallback, useEffect} from 'react';
import { strings } from 'src/localization';
import { DateTime } from '@reynolds/datetime';
import { IDMDocs, IdmUuids, ServiceDocument } from 'src/models';
import DocumentTile from '../DocumentTile/DocumentTile';
import SaveAs from 'src/components/SaveAs/SaveAs';
import * as css from './DocumentViewer.module.scss';
import { Button } from '@framework-controls/core';
import classNames from 'classnames';

interface IProps {
    specs: ServiceDocument[],
    documents: IDMDocs[],
    idmPdfUUIDs: IdmUuids[],
    previewFlag: string,
    idmFile: string,
    idmFiles: string[],
    roNum: string,
    roDate: string,
    docTimeRef: Date,
    theme: string,
    colorSensitiveEnabled: boolean;
    setRefreshMsg: (open: boolean) => void;
    setIVModalFlag: (open: boolean) => void;
    getIDMdocs: (idmPdfUUIDs: any) => void;
    getIDMdoc: (docUUID: string, elementUUID: string) => void;
    clearIdmFile: () => void;
    logDocsHistory: (keyRO: string, action: string, docType: string, details: string, idmUrl: string) => void;
    addCUC: (cucNum: number) => void;
}

// this should be in models probably
type ServiceDocType = 'RO' | 'SE' | 'MM' | 'VI' | 'RC' | 'VC' | 'IN' | 'IV' | 'HU';

const DocumentViewer = (props: IProps) => {
    const [selectedDocs, setSelectedDocs] = useState<ServiceDocType[]>([]);
    const [viewedDocs, setViewedDocs] = useState<ServiceDocType[]>([]);
    const [saveAsDownloadList, setSaveAsDownloadList] = useState<string[]>([]);
    const [saveAsDefaultFileNames, setSaveAsDefaultFileNames] = useState<string[]>([]);
    const [saveAsOpenFlag, setSaveAsOpenFlag] = useState<boolean>(false);
    const [gettingPdfSave, setGettingPdfSave] = useState<boolean>(false);
    const [gettingPdfSaveDone, setGettingPdfSaveDone] = useState<boolean>(false);
    const [savePdfIndex, setSavePdfIndex] = useState<number>(-1);

    useEffect(() => {
        props.getIDMdocs(props.idmPdfUUIDs);
    }, [props.getIDMdocs, props.idmPdfUUIDs]);

    const onToggle = useCallback((type: ServiceDocType, checked: boolean) => {
        const checkedArray = [...selectedDocs];
        if (checked) {
            setSelectedDocs(checkedArray.filter(d => d !== type));
        } else {
            checkedArray.push(type);
            setSelectedDocs(checkedArray);
        }
    }, [selectedDocs]);

    const trackCUC = useCallback((type: ServiceDocType) => {
        let cucNum = 0;
        // track document viewed related CUCs and increment them
        switch (type) {
            case 'RO':
                cucNum = 861;
                break;
            case 'SE':
                cucNum = 864;
                break;
            case 'MM':
                cucNum = 863;
                break;
            case 'VI':
                cucNum = 862;
                break;
            case 'RC':
                cucNum = 865;
                break;
            case 'VC':
                cucNum = 866;
                break;
            case 'IN':
                cucNum = 867;
                break;
            case 'IV':
                cucNum = 868;
                break;
            default:
                break;
        }
        props.addCUC(cucNum);
    }, [props.addCUC]);

    const onView = (type: ServiceDocType, checked: boolean) => {
        const checkedArray = [...viewedDocs];
        if (!checked) {
            checkedArray.push(type);
            setViewedDocs(checkedArray);
        }
        if ((Math.abs(props.docTimeRef.getTime() - new Date().getTime()) / 36e5) >= 0.95) {
            // session is expired and user needs to refresh
            props.setRefreshMsg(true);

            return;
        }
        // if Pictures/Videos is selected open modal otherwise open pdf document
        if (type === 'IV') {
            props.setIVModalFlag(true);
            if (!props.previewFlag) {
                props.logDocsHistory(props.roNum, 'DV', 'IV', '', '');
                trackCUC(type);
            }
        } else {
            const uuidIndex = props.idmPdfUUIDs.findIndex((d) => d.type === type);
            if (uuidIndex >= 0 && props.idmFiles[uuidIndex]) {
                window.open(props.idmFiles[uuidIndex]);
                if (!props.previewFlag) {
                    props.logDocsHistory(props.roNum, 'DV', type, '', '');
                    trackCUC(type);
                }
            }
        }
    };

    const documentTiles = props.idmPdfUUIDs.map(doc => {
        const serviceDocument = props.specs.find(d => d.type === doc.type);
        const isChecked = selectedDocs.some(d => d === doc.type);
        const isViewed = viewedDocs.some(d => d === doc.type);
        const docForDate = props.documents.find(d => d.type === doc.type);
        const displayDate = docForDate ? strings.formatString(strings.OSDDocDisplayDate,
            docForDate.time, docForDate.date).toString():'';

        return (
            <DocumentTile
                key={doc.type}
                type={doc.type}
                name={serviceDocument?.displayName ? serviceDocument.displayName:''}
                desc={serviceDocument?.description ? serviceDocument.description:''}
                updatedDate={displayDate}
                viewed={isViewed}
                checked={isChecked}
                onToggle={onToggle}
                onView = {onView}
                colorSensitiveEnabled={props.colorSensitiveEnabled}
                theme={props.theme} />
        );
    });

    const downloadHandler = useCallback(() => {
        if ((Math.abs(props.docTimeRef.getTime() - new Date().getTime()) / 36e5) >= 0.95) {
            // session is expired and user needs to refresh
            props.setRefreshMsg(true);
        } else {
            // need to loop for each checked file service document.
            setGettingPdfSave(true);
            setGettingPdfSaveDone(false);
            setSaveAsDownloadList([]);
            setSaveAsDefaultFileNames([]);
            setSavePdfIndex(selectedDocs.length-1);
        }
    }, [selectedDocs, props.docTimeRef, props.setRefreshMsg]);

    // grabs pdfs using selectedDocs and props.idmPDFUUIDs for downloading
    useEffect(() => {
        if (gettingPdfSave && savePdfIndex >= 0) {
            const currentDoc = selectedDocs[savePdfIndex];
            const pdfIndex = props.idmPdfUUIDs.findIndex(d => d.type === currentDoc);
            props.getIDMdoc(props.idmPdfUUIDs[pdfIndex].docUuid, props.idmPdfUUIDs[pdfIndex].elementUuid);
        }
    }, [gettingPdfSave, savePdfIndex, selectedDocs, props.idmPdfUUIDs, props.getIDMdoc]);

    // stores list of pdf blobs for the Save As window
    useEffect(() => {
        if (gettingPdfSave && props.idmFile && savePdfIndex >= 0) {
            const saveAsUrls: string[] = saveAsDownloadList;
            saveAsUrls[savePdfIndex] = (props.idmFile);
            setSaveAsDownloadList(saveAsUrls);
            props.clearIdmFile();
            setSavePdfIndex(savePdfIndex - 1);
            if (savePdfIndex-1 < 0) {
                setGettingPdfSaveDone(true);
            }
        }
    }, [gettingPdfSave, savePdfIndex, props.idmFile, saveAsDownloadList, props.clearIdmFile]);

    useEffect(() => {
        if (selectedDocs.length > 0 && savePdfIndex <= 0 && gettingPdfSaveDone) {
            const saveAsDefFileNames: string[] = [];
            selectedDocs.forEach((x) => {
                const nameLocation = props.specs.findIndex(spec => spec.type === x);
                const displayName = props.specs[nameLocation].displayName;
                const fileNameDate = new DateTime(new Date(props.roDate)).format('MMDDYY');
                // Default name should be DocumentDisplayName_RODate
                const imgFileName = displayName+ '_' + fileNameDate;
                saveAsDefFileNames.push(imgFileName);
            });
            // set Save As dialog props and open it.
            setSaveAsDefaultFileNames(saveAsDefFileNames);
            setSaveAsOpenFlag(true);
            setGettingPdfSaveDone(false);
        }
    }, [selectedDocs, savePdfIndex, gettingPdfSaveDone, props.specs, props.idmPdfUUIDs, props.roDate]);

    const saveAsCloseHandler = useCallback(() => {
        setSaveAsOpenFlag(false);
        setSelectedDocs([]);
    }, []);


    const downloadBtnStyles = classNames(
        css.downloadBtn,
        {
            [css.downloadBtnBMW]: props.theme === 'bmw',
        },
    );


    return (
        <div className={css.flexbox}>
            {documentTiles}
            <div className={downloadBtnStyles}>
                <Button
                    id='downloadBtn'
                    onClick={downloadHandler}
                    variant={props.theme === 'infiniti' ? 'downloadAlt' :'download'}
                    disabled={selectedDocs.length === 0 || props.previewFlag === 'p'}
                >
                    { strings.DownloadSelected }
                </Button>
            </div>
            {
                saveAsOpenFlag ?
                    <SaveAs
                        downloadList={saveAsDownloadList}
                        defaultFileNames={saveAsDefaultFileNames}
                        closeHandler={saveAsCloseHandler}
                        theme={props.theme}
                    />: null
            }
        </div>
    );
};

export default DocumentViewer;
